import React, { useState } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Zap,
  Users,
  Shield,
  Wrench,
  Building,
  CreditCard,
  Truck,
  ChevronRight,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { Card } from '../ui/Card';

interface ExpenseAnalysisDashboardProps {
  data: any;
}

interface ExpenseCategory {
  name: string;
  icon: React.ElementType;
  subjectAmount: number;
  surveyAverage: number;
  percentileRank: number;
  yearOverYear: number;
  breakdown: {
    subcategory: string;
    amount: number;
    percentage: number;
    benchmark: number;
    opportunity: string;
  }[];
}

export default function ExpenseAnalysisDashboard({ data }: ExpenseAnalysisDashboardProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('labor');

  const expenseCategories: ExpenseCategory[] = [
    {
      name: 'Labor Costs',
      icon: Users,
      subjectAmount: 180000,
      surveyAverage: 195000,
      percentileRank: 72,
      yearOverYear: -2.5,
      breakdown: [
        { subcategory: 'Manager Salaries', amount: 65000, percentage: 36, benchmark: 68000, opportunity: 'Competitive rates maintained' },
        { subcategory: 'Hourly Staff', amount: 88000, percentage: 49, benchmark: 92000, opportunity: 'Efficient scheduling optimization' },
        { subcategory: 'Benefits & Payroll Tax', amount: 27000, percentage: 15, benchmark: 35000, opportunity: 'Cost-effective benefits package' }
      ]
    },
    {
      name: 'Utilities',
      icon: Zap,
      subjectAmount: 48000,
      surveyAverage: 52000,
      percentileRank: 68,
      yearOverYear: -8.2,
      breakdown: [
        { subcategory: 'Electricity', amount: 28800, percentage: 60, benchmark: 32000, opportunity: 'LED upgrade showing savings' },
        { subcategory: 'Gas/Heating', amount: 12000, percentage: 25, benchmark: 13000, opportunity: 'Efficient HVAC system' },
        { subcategory: 'Water/Sewer', amount: 7200, percentage: 15, benchmark: 7000, opportunity: 'Monitor usage patterns' }
      ]
    },
    {
      name: 'Insurance',
      icon: Shield,
      subjectAmount: 36000,
      surveyAverage: 42000,
      percentileRank: 75,
      yearOverYear: -5.1,
      breakdown: [
        { subcategory: 'General Liability', amount: 18000, percentage: 50, benchmark: 22000, opportunity: 'Strong safety record benefits' },
        { subcategory: 'Property Insurance', amount: 12000, percentage: 33, benchmark: 14000, opportunity: 'Well-maintained property' },
        { subcategory: 'Environmental', amount: 6000, percentage: 17, benchmark: 6000, opportunity: 'Industry standard coverage' }
      ]
    },
    {
      name: 'Repairs & Maintenance',
      icon: Wrench,
      subjectAmount: 42000,
      surveyAverage: 38000,
      percentileRank: 35,
      yearOverYear: 12.5,
      breakdown: [
        { subcategory: 'Equipment Repairs', amount: 18900, percentage: 45, benchmark: 15000, opportunity: 'Consider preventive maintenance program' },
        { subcategory: 'Building Maintenance', amount: 12600, percentage: 30, benchmark: 12000, opportunity: 'Aging infrastructure impact' },
        { subcategory: 'Ground/Landscaping', amount: 10500, percentage: 25, benchmark: 11000, opportunity: 'Well-maintained grounds' }
      ]
    },
    {
      name: 'Property Tax',
      icon: Building,
      subjectAmount: 28000,
      surveyAverage: 25000,
      percentileRank: 25,
      yearOverYear: 4.2,
      breakdown: [
        { subcategory: 'Real Property Tax', amount: 22400, percentage: 80, benchmark: 20000, opportunity: 'Consider assessment appeal' },
        { subcategory: 'Personal Property Tax', amount: 3360, percentage: 12, benchmark: 3000, opportunity: 'Equipment valuation review' },
        { subcategory: 'Special Assessments', amount: 2240, percentage: 8, benchmark: 2000, opportunity: 'Municipal development fees' }
      ]
    },
    {
      name: 'Credit Card Processing',
      icon: CreditCard,
      subjectAmount: 24000,
      surveyAverage: 22000,
      percentileRank: 40,
      yearOverYear: 8.8,
      breakdown: [
        { subcategory: 'Fuel Transaction Fees', amount: 14400, percentage: 60, benchmark: 13000, opportunity: 'Negotiate better rates with volume' },
        { subcategory: 'Store Transaction Fees', amount: 7200, percentage: 30, benchmark: 6500, opportunity: 'Promote cash/debit usage' },
        { subcategory: 'Monthly Service Fees', amount: 2400, percentage: 10, benchmark: 2500, opportunity: 'Competitive service fees' }
      ]
    }
  ];

  const getPerformanceColor = (percentileRank: number, yearOverYear: number, isExpense: boolean = true) => {
    // For expenses, lower is better, so flip the logic
    const effectiveRank = isExpense ? 100 - percentileRank : percentileRank;

    if (effectiveRank >= 75) return 'text-green-600';
    if (effectiveRank >= 50) return 'text-blue-600';
    if (effectiveRank >= 25) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceBg = (percentileRank: number, isExpense: boolean = true) => {
    const effectiveRank = isExpense ? 100 - percentileRank : percentileRank;

    if (effectiveRank >= 75) return 'bg-green-50 border-green-200';
    if (effectiveRank >= 50) return 'bg-blue-50 border-blue-200';
    if (effectiveRank >= 25) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  const getTrendIcon = (yearOverYear: number) => {
    return yearOverYear > 0 ? TrendingUp : TrendingDown;
  };

  const getTrendColor = (yearOverYear: number, isExpense: boolean = true) => {
    // For expenses, negative trend (reduction) is good
    const isGoodTrend = isExpense ? yearOverYear < 0 : yearOverYear > 0;
    return isGoodTrend ? 'text-green-600' : 'text-red-600';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const currentCategory = expenseCategories.find(cat =>
    cat.name.toLowerCase().includes(selectedCategory)
  ) || expenseCategories[0];

  const totalExpenses = expenseCategories.reduce((sum, cat) => sum + cat.subjectAmount, 0);
  const totalSurveyAverage = expenseCategories.reduce((sum, cat) => sum + cat.surveyAverage, 0);

  return (
    <div className="space-y-6">
      {/* Header Summary */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Operating Expense Analysis
            </h2>
            <p className="text-gray-600">
              Detailed breakdown of expenses vs industry benchmarks with improvement opportunities
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold text-red-600">
              {formatCurrency(totalExpenses)}
            </div>
            <div className="text-sm text-gray-500">Total Annual Expenses</div>
            <div className={`text-sm font-medium ${getTrendColor((totalExpenses - totalSurveyAverage) / totalSurveyAverage * 100)}`}>
              {totalExpenses < totalSurveyAverage ?
                `${formatCurrency(totalSurveyAverage - totalExpenses)} below average` :
                `${formatCurrency(totalExpenses - totalSurveyAverage)} above average`
              }
            </div>
          </div>
        </div>
      </div>

      {/* Category Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {expenseCategories.map((category) => {
          const Icon = category.icon;
          const TrendIcon = getTrendIcon(category.yearOverYear);

          return (
            <div
              key={category.name}
              className="cursor-pointer transition-all hover:shadow-md"
              onClick={() => setSelectedCategory(category.name.toLowerCase().split(' ')[0])}
            >
              <Card
                className={`p-4 ${
                  currentCategory.name === category.name ? 'ring-2 ring-blue-500' : ''
                } ${getPerformanceBg(category.percentileRank)}`}
              >
              <div className="flex items-center justify-between mb-3">
                <Icon className="w-6 h-6 text-gray-600" />
                <div className="flex items-center gap-1">
                  <TrendIcon className={`w-4 h-4 ${getTrendColor(category.yearOverYear)}`} />
                  <span className={`text-sm font-medium ${getTrendColor(category.yearOverYear)}`}>
                    {Math.abs(category.yearOverYear).toFixed(1)}%
                  </span>
                </div>
              </div>

              <h3 className="font-semibold text-gray-900 mb-1">{category.name}</h3>
              <div className="text-2xl font-bold text-gray-900 mb-1">
                {formatCurrency(category.subjectAmount)}
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600">
                  vs {formatCurrency(category.surveyAverage)} avg
                </span>
                <span className={`font-medium ${getPerformanceColor(category.percentileRank, category.yearOverYear)}`}>
                  {100 - category.percentileRank}th percentile
                </span>
              </div>
              </Card>
            </div>
          );
        })}
      </div>

      {/* Detailed Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Category Breakdown */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900">
              {currentCategory.name} Breakdown
            </h3>
            <div className="text-right">
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(currentCategory.subjectAmount)}
              </div>
              <div className="text-sm text-gray-500">Annual Total</div>
            </div>
          </div>

          <div className="space-y-4">
            {currentCategory.breakdown.map((item, index) => (
              <div key={index} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.subcategory}</h4>
                  <div className="text-right">
                    <div className="font-bold text-gray-900">
                      {formatCurrency(item.amount)}
                    </div>
                    <div className="text-sm text-gray-500">
                      {item.percentage}% of category
                    </div>
                  </div>
                </div>

                {/* Progress bar showing percentage of category */}
                <div className="mb-3">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500"
                      style={{width: `${item.percentage}%`}}
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">
                    Benchmark: {formatCurrency(item.benchmark)}
                  </span>
                  <div className="flex items-center gap-1">
                    {item.amount < item.benchmark ? (
                      <CheckCircle2 className="w-4 h-4 text-green-600" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-yellow-600" />
                    )}
                    <span className={item.amount < item.benchmark ? 'text-green-600' : 'text-yellow-600'}>
                      {item.amount < item.benchmark ? 'Below' : 'Above'} benchmark
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-600 mt-2 italic">
                  {item.opportunity}
                </p>
              </div>
            ))}
          </div>
        </Card>

        {/* Expense Comparison Chart */}
        <Card className="p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">
            Industry Comparison Overview
          </h3>

          <div className="space-y-4">
            {expenseCategories.map((category, index) => {
              const percentageOfTotal = (category.subjectAmount / totalExpenses) * 100;
              const benchmarkPercentage = (category.surveyAverage / totalSurveyAverage) * 100;
              const variance = category.subjectAmount - category.surveyAverage;

              return (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{category.name}</span>
                    <div className="text-right">
                      <div className="font-bold">
                        {formatCurrency(category.subjectAmount)}
                      </div>
                      <div className={`text-sm font-medium ${variance < 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {variance < 0 ? '' : '+'}${Math.abs(variance).toLocaleString()} vs benchmark
                      </div>
                    </div>
                  </div>

                  {/* Comparison bars */}
                  <div className="space-y-1">
                    {/* Your expense */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 w-16">Yours:</span>
                      <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500"
                          style={{width: `${Math.min(percentageOfTotal, 100)}%`}}
                        />
                      </div>
                      <span className="text-xs text-gray-600 w-12">
                        {percentageOfTotal.toFixed(1)}%
                      </span>
                    </div>

                    {/* Benchmark */}
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-600 w-16">Benchmark:</span>
                      <div className="flex-1 h-4 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gray-400"
                          style={{width: `${Math.min(benchmarkPercentage, 100)}%`}}
                        />
                      </div>
                      <span className="text-xs text-gray-600 w-12">
                        {benchmarkPercentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary insights */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-900 mb-2">Key Insights</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Strong cost control in labor and utilities</li>
              <li>• Above-average repairs indicate aging equipment</li>
              <li>• Property tax higher due to premium location</li>
              <li>• Credit card fees increasing with volume growth</li>
            </ul>
          </div>
        </Card>
      </div>
    </div>
  );
}